// src/oura/components/client/askTargets.ts
//
// What the Client tab's ask drawer opens, and the group a row opened it
// from: a question, a change request, a call or a pack, each known by a key
// ("item:<id>"), and the snapshot of its group that previous and next walk.

import type { ChangeRequest } from "@service/aeo/clientReview";
import { DOC_STATUS, type Tone } from "@oura/ui";
import type { AskGroups } from "./asks";
import type { RequestGroups } from "./changeRequests";

/**
 * What the drawer opens. A change may carry the row's copy of the request, so
 * a sheet opened from a link or a row never waits on the next read.
 */
export type AskTarget =
    | { kind: "item"; itemId: string }
    | { kind: "call"; callId: string }
    | { kind: "change"; changeId: string; change?: ChangeRequest }
    | { kind: "pack"; packId: string };

/**
 * The group a row sat in when it opened the drawer, as it stood then: the
 * GroupFold's id ("asks.needs_us"), its word ("Needs us") and its rows as
 * keys (askKey) in on-screen order. Previous and next walk it.
 */
export interface AskSeq {
    groupId: string;
    label: string;
    keys: string[];
}

/** "item:<id>", "change:<id>", "call:<id>", "pack:<id>". */
export function askKey(t: AskTarget): string {
    switch (t.kind) {
        case "item":
            return `item:${t.itemId}`;
        case "call":
            return `call:${t.callId}`;
        case "change":
            return `change:${t.changeId}`;
        default:
            return `pack:${t.packId}`;
    }
}

export function keyTarget(key: string): AskTarget {
    const at = key.indexOf(":");
    const kind = key.slice(0, at);
    const id = key.slice(at + 1);
    if (kind === "call") return { kind: "call", callId: id };
    if (kind === "change") return { kind: "change", changeId: id };
    if (kind === "pack") return { kind: "pack", packId: id };
    return { kind: "item", itemId: id };
}

/** The group heads' words and dot tones, as the two cards show them. */
export const ASK_GROUP: Record<string, { label: string; tone: Tone }> = {
    "asks.needs_us": { label: DOC_STATUS.needs_us.staff.label, tone: DOC_STATUS.needs_us.staff.tone },
    "asks.waiting": { label: DOC_STATUS.waiting_on_client.staff.label, tone: DOC_STATUS.waiting_on_client.staff.tone },
    "asks.answered": { label: "Answered", tone: "green" },
    "asks.not_sent": { label: DOC_STATUS.not_sent_yet.staff.label, tone: DOC_STATUS.not_sent_yet.staff.tone },
    "requests.needs_us": { label: "Needs us", tone: DOC_STATUS.needs_us.staff.tone },
    "requests.scheduled": { label: "Calls scheduled", tone: "green" },
    "requests.done": { label: "Done", tone: "green" },
    "packs.sent": { label: "Sent", tone: "green" },
};

/** A group's snapshot, its rows in the order the card shows them. */
export function askSeqFor(groupId: string, groups: AskGroups, requests: RequestGroups): AskSeq {
    const items = (list: AskGroups["needsUs"]) => list.map((a) => `item:${a.item.id}`);
    const reqs = (list: RequestGroups["needsUs"]) => list.map((r) => `${r.kind}:${r.id}`);
    const keys =
        groupId === "asks.needs_us"
            ? items(groups.needsUs)
            : groupId === "asks.waiting"
                ? items(groups.waiting)
                : groupId === "asks.answered"
                    ? items(groups.answered)
                    : groupId === "requests.needs_us"
                        ? reqs(requests.needsUs)
                        : groupId === "requests.scheduled"
                            ? reqs(requests.scheduled)
                            : groupId === "requests.done"
                                ? reqs(requests.done)
                                : [];
    return { groupId, label: ASK_GROUP[groupId]?.label ?? "", keys };
}
